export type DocumentVariableTuple = readonly [token: string, label: string];

export type DocumentVariableGroupSpec = readonly [
    key: string,
    labelKey: string,
    variables: ReadonlyArray<DocumentVariableTuple>,
];

export interface DocumentVariableGroup {
    key: string;
    label: string;
    variables: Array<{ token: string; label: string }>;
}

const defineDocumentVariableGroup = (
    key: string,
    labelKey: string,
    variables: ReadonlyArray<DocumentVariableTuple>,
): DocumentVariableGroupSpec => [key, labelKey, variables];

export const CONTRACT_DOCUMENT_VARIABLE_GROUP_SPECS: ReadonlyArray<DocumentVariableGroupSpec> = [
    defineDocumentVariableGroup(
        'contract',
        'configuration.contractDocument.variables.groupContract',
        [
            ['{{Contract.Nr}}', 'Nr.'],
            ['{{Contract.Data}}', 'Data'],
            ['{{Contract.Suma}}', 'Suma'],
            ['{{Contract.InLitere}}', 'In litere'],
            ['{{Contract.Durata}}', 'Durata'],
            ['{{Contract.Comision}}', 'Comision'],
            ['{{Contract.Personal}}', 'Personal'],
            ['{{Contract.TertaPersoana}}', 'De acord terta persoana (DA/NU)'],
            ['{{Contract.Statut}}', 'Statut'],
        ],
    ),
    defineDocumentVariableGroup('client', 'configuration.contractDocument.variables.groupClient', [
        ['{{Client.Nume}}', 'Nume'],
        ['{{Client.Localitate}}', 'Localitate'],
        ['{{Client.Strada}}', 'Strada'],
        ['{{Client.Nr}}', 'Nr.'],
        ['{{Client.Ap}}', 'Ap.'],
        ['{{Client.Judet}}', 'Judet'],
        ['{{Client.BSeria}}', 'B.I. seria'],
        ['{{Client.BNr}}', 'B.I. nr.'],
        ['{{Client.Politia}}', 'Politia'],
        ['{{Client.CNP}}', 'CNP'],
        ['{{Client.Telefon}}', 'Telefon'],
        ['{{Client.Email}}', 'Email'],
    ]),
    defineDocumentVariableGroup(
        'company',
        'configuration.contractDocument.variables.groupCompany',
        [
            ['{{Firma.Denumire}}', 'Denumire'],
            ['{{Firma.Localitate}}', 'Localitate'],
            ['{{Firma.Strada}}', 'Strada'],
            ['{{Firma.Nr}}', 'Nr.'],
            ['{{Firma.J}}', 'J'],
            ['{{Firma.CUI}}', 'CUI'],
            ['{{Firma.CapitalSocial}}', 'Capital social'],
            ['{{Firma.Reprezentant}}', 'Reprezentant'],
            ['{{Firma.InCalitateDe}}', 'In calitate de'],
        ],
    ),
    defineDocumentVariableGroup(
        'objects',
        'configuration.contractDocument.variables.groupObjects',
        [
            ['{{ObjectsTable}}', 'Tabel obiecte (auto)'],
            ['{{asset.logo}}', 'Logo'],
            ['{{asset.page1_background}}', 'Fundal pagina 1'],
            ['{{asset.page2_background}}', 'Fundal pagina 2'],
        ],
    ),
];

export const INVOICE_DOCUMENT_VARIABLE_GROUP_SPECS: ReadonlyArray<DocumentVariableGroupSpec> = [
    defineDocumentVariableGroup('invoice', 'configuration.invoiceDocument.variables.groupInvoice', [
        ['{{Factura.Nr}}', 'number'],
        ['{{Factura.Data}}', 'date'],
        ['{{Factura.Ora}}', 'time'],
        ['{{Factura.ContractNr}}', 'contract'],
        ['{{Factura.Tip}}', 'type'],
        ['{{Factura.Total}}', 'total'],
        ['{{Factura.InLitere}}', 'amountInWords'],
        ['{{Factura.PlataElectronica}}', 'electronicPayment'],
        ['{{ItemsTable}}', 'itemsTable'],
    ]),
    defineDocumentVariableGroup('client', 'configuration.invoiceDocument.variables.groupClient', [
        ['{{Client.Nume}}', 'name'],
        ['{{Client.CNP}}', 'personalCode'],
        ['{{Client.Adresa}}', 'address'],
        ['{{Client.ActIdentitate}}', 'identityDocument'],
    ]),
    defineDocumentVariableGroup('company', 'configuration.invoiceDocument.variables.groupCompany', [
        ['{{Firma.Denumire}}', 'name'],
        ['{{Firma.J}}', 'registrationNumber'],
        ['{{Firma.CUI}}', 'taxId'],
        ['{{Firma.Adresa}}', 'address'],
        ['{{Firma.IBAN}}', 'iban'],
        ['{{Firma.Banca}}', 'bank'],
    ]),
    defineDocumentVariableGroup(
        'operator',
        'configuration.invoiceDocument.variables.groupOperator',
        [
            ['{{Operator.Nume}}', 'name'],
            ['{{Operator.ActIdentitate}}', 'identityDocument'],
        ],
    ),
];

export const buildDocumentVariableGroups = (
    groupSpecs: ReadonlyArray<DocumentVariableGroupSpec>,
    translate: (key: string) => string,
    resolveVariableLabel: (label: string) => string,
): DocumentVariableGroup[] =>
    groupSpecs.map(([key, labelKey, variables]) => ({
        key,
        label: translate(labelKey),
        variables: variables.map(([token, label]) => ({
            token,
            label: resolveVariableLabel(label),
        })),
    }));
